import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { NotFound } from '@/components/NotFound';
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute';

import { StudioHeader } from './components/StudioHeader';
import { StudioSidebar } from './components/StudioSidebar';
import { MorningDashboard } from './morning/MorningDashboard';
import { SourceRegistry } from './sources/SourceRegistry';
import { ErrorQueue } from './errors/ErrorQueue';
import { ErrorDetail } from './errors/ErrorDetail';
import { GraphExplorer } from './explorer/GraphExplorer';
import { HelpCenter } from './help/HelpCenter';
import { PipelineRuns } from './supervision/PipelineRuns';
import { OntologyBrowser } from './ontology/OntologyBrowser';
import { OntologyGapQueue } from './ontology/OntologyGapQueue';
import { OntologyDraftEditor } from './ontology/OntologyDraftEditor';
import { SparqlEditor } from './ontology/SparqlEditor';
import { SkillLibrary } from './skills/SkillLibrary';
import { SkillEditor } from './skills/SkillEditor';
import { ManualCuration } from './curation/ManualCuration';
import { GISInput } from './gis/GISInput';
import { InferenceRules } from './inference/InferenceRules';
import { ExtensionSetup } from './extension/ExtensionSetup';
import { IngestPaste } from './ingest/IngestPaste';

const STUDIO_MIN_WIDTH = 1280;

/**
 * Back-office Studio shell — desktop only ≥1280px (DEC-07).
 *
 * Auth: requires an internal role (analyst / PO / admin). External clients
 * never reach this shell — the role-routing redirects them at login.
 */
export function StudioShell() {
  const tooNarrow = useTooNarrow(STUDIO_MIN_WIDTH);

  if (tooNarrow) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-center">
        <p className="text-body text-slate-600">
          Studio requires a screen at least 1280px wide.
          <br />
          Please open this page on a desktop.
        </p>
      </div>
    );
  }

  return (
    <ProtectedRoute
      roles={[
        'data_analyst_standard',
        'data_analyst_domain_expert',
        'administrator',
      ]}
    >
      <div className="flex h-screen bg-slate-50">
        <StudioSidebar />
        <div className="flex flex-1 flex-col">
          <StudioHeader />
          <main className="flex-1 overflow-y-auto">
            <Routes>
              <Route index element={<Navigate to="morning" replace />} />
              <Route path="morning" element={<MorningDashboard />} />
              <Route path="pipeline-runs" element={<PipelineRuns />} />
              <Route path="sources" element={<SourceRegistry />} />
              <Route path="errors" element={<ErrorQueue />} />
              <Route path="errors/:errorId" element={<ErrorDetail />} />
              <Route
                path="ontology"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert']}>
                    <OntologyGapQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="ontology/browser"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert', 'internal_front_office', 'administrator']}>
                    <OntologyBrowser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="ontology/sparql"
                element={
                  <ProtectedRoute roles={['data_analyst_standard', 'data_analyst_domain_expert', 'administrator']}>
                    <SparqlEditor />
                  </ProtectedRoute>
                }
              />
              <Route path="explorer" element={<GraphExplorer />} />
              <Route path="help" element={<HelpCenter />} />
              <Route
                path="ontology/drafts/:draftId"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert']}>
                    <OntologyDraftEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="inference/*"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert']}>
                    <InferenceRules />
                  </ProtectedRoute>
                }
              />
              <Route
                path="skills"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert']}>
                    <SkillLibrary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="skills/:skillId"
                element={
                  <ProtectedRoute roles={['data_analyst_domain_expert']}>
                    <SkillEditor />
                  </ProtectedRoute>
                }
              />
              <Route path="curation" element={<ManualCuration />} />
              <Route path="gis" element={<GISInput />} />
              <Route path="ingest" element={<IngestPaste />} />
              <Route path="extension" element={<ExtensionSetup />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}

function useTooNarrow(minWidth: number): boolean {
  const [narrow, setNarrow] = useState(() => window.innerWidth < minWidth);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < minWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth]);
  return narrow;
}
